import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  Lock, LogOut, RefreshCw, Search, Mail, Phone, Building2, MessageSquare,
  Calendar, Tag, BarChart3, ArrowRight, Loader2, AlertCircle, ChevronDown, ChevronUp,
} from "lucide-react";
import Chevron from "@/components/Chevron";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "delfi.admin.token";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_delfi-marketing/artifacts/wqgfxt9m_delfi_logo_white.png";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return iso || "—";
  }
}

function LoginScreen({ onAuth }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data?.token) {
        localStorage.setItem(STORAGE_KEY, res.data.token);
        onAuth(res.data.token);
      } else {
        setError("Unexpected response from server");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-delfi-dark flex items-center justify-center px-6 relative overflow-hidden" data-testid="admin-login">
      <div className="chevron-bg right-[-10%] top-[-18%] opacity-[0.08]" aria-hidden="true">›</div>
      <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
      <div className="hero-glow left-[-200px] top-[-180px]" aria-hidden="true" />
      <div className="hero-glow right-[-260px] bottom-[-260px] opacity-70" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Twasal Awasal" className="h-12 w-auto mx-auto mb-6" />
          <div className="inline-flex items-center gap-2 text-delfi-red text-xs tracking-[0.2em] uppercase font-semibold">
            <Chevron size={14} /> Admin Console
          </div>
          <h1 className="mt-3 text-3xl font-extrabold text-white tracking-tight">
            Enquiries Dashboard
          </h1>
          <p className="mt-2 text-white/60 text-sm">Enter the admin password to continue.</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white/[0.04] border border-white/10 backdrop-blur rounded-2xl p-6"
          data-testid="admin-login-form"
        >
          <label className="block text-xs font-bold uppercase tracking-wider text-white/70 mb-2">
            Admin password
          </label>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/45" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              autoFocus
              className="w-full bg-white/5 border border-white/15 rounded-xl pl-10 pr-3 py-3 text-white placeholder-white/30 focus:outline-none focus:border-delfi-red transition-colors"
              data-testid="admin-login-password"
            />
          </div>

          {error && (
            <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-delfi-red/15 border border-delfi-red/30 text-delfi-red text-sm" data-testid="admin-login-error">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-5 btn-delfi-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
            data-testid="admin-login-submit"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in...</> : <>Sign in <ArrowRight size={16} /></>}
          </button>
        </form>

        <p className="mt-6 text-center text-white/40 text-xs">
          Twasal Awasal · Internal tool
        </p>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-delfi-navy/8 p-5 shadow-[0_2px_8px_-2px_rgba(46,46,46,0.08)]">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-delfi-red/10 text-delfi-red flex items-center justify-center">
          <Icon size={18} />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-delfi-navy/55">{label}</p>
          <p className="text-2xl font-extrabold text-delfi-navy tracking-tight mt-0.5">{value}</p>
        </div>
      </div>
    </div>
  );
}

function EnquiryRow({ row, expanded, onToggle }) {
  return (
    <>
      <tr
        className="border-b border-delfi-cloud cursor-pointer hover:bg-delfi-cloud/40 transition-colors"
        onClick={onToggle}
        data-testid={`enquiry-row-${row.id}`}
      >
        <td className="px-4 py-3.5 text-xs text-delfi-navy/60 whitespace-nowrap">{formatDate(row.created_at)}</td>
        <td className="px-4 py-3.5">
          <p className="text-sm font-bold text-delfi-navy">{row.company}</p>
          <p className="text-xs text-delfi-navy/55 mt-0.5">{row.name}</p>
        </td>
        <td className="px-4 py-3.5">
          <a
            href={`mailto:${row.email}`}
            onClick={(e) => e.stopPropagation()}
            className="text-sm text-delfi-red hover:underline"
          >
            {row.email}
          </a>
          {row.phone && <p className="text-xs text-delfi-navy/55 mt-0.5" dir="ltr">{row.phone}</p>}
        </td>
        <td className="px-4 py-3.5">
          {row.plan_interest ? (
            <span className="inline-block px-2.5 py-1 rounded-full bg-delfi-red/10 text-delfi-red text-xs font-bold">
              {row.plan_interest}
            </span>
          ) : (
            <span className="text-delfi-navy/35 text-xs">—</span>
          )}
        </td>
        <td className="px-4 py-3.5 text-xs text-delfi-navy/70">{row.monthly_volume || "—"}</td>
        <td className="px-4 py-3.5 text-right text-delfi-navy/45">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-delfi-cloud/60">
          <td colSpan={6} className="px-6 py-5" data-testid={`enquiry-detail-${row.id}`}>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-6">
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <Building2 size={14} className="text-delfi-red mt-1 shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-delfi-navy/55">Company</p>
                    <p className="text-delfi-navy font-semibold">{row.company}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Mail size={14} className="text-delfi-red mt-1 shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-delfi-navy/55">Email</p>
                    <a href={`mailto:${row.email}`} className="text-delfi-red font-semibold hover:underline">{row.email}</a>
                  </div>
                </div>
                {row.phone && (
                  <div className="flex items-start gap-2">
                    <Phone size={14} className="text-delfi-red mt-1 shrink-0" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-delfi-navy/55">Phone</p>
                      <a href={`tel:${row.phone.replace(/\s+/g, "")}`} className="text-delfi-navy font-semibold" dir="ltr">{row.phone}</a>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <Tag size={14} className="text-delfi-red mt-1 shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-delfi-navy/55">Enquiry ID</p>
                    <code className="text-xs text-delfi-navy/70">{row.id}</code>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-delfi-navy/55 flex items-center gap-1">
                  <MessageSquare size={12} /> Message
                </p>
                <div className="mt-2 p-4 bg-white border-l-4 border-delfi-red rounded-lg text-sm text-delfi-navy leading-relaxed whitespace-pre-wrap">
                  {row.message}
                </div>
                <div className="mt-4 flex gap-2">
                  <a
                    href={`mailto:${row.email}?subject=Re: Your Twasal Awasal enquiry`}
                    className="btn-delfi-primary text-sm"
                  >
                    <Mail size={14} /> Reply
                  </a>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Dashboard({ token, onLogout }) {
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, last_7_days: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/partners?limit=500`, { headers }),
        axios.get(`${API}/admin/partners/stats`, { headers }),
      ]);
      setEnquiries(listRes.data || []);
      setStats(statsRes.data || { total: 0, last_7_days: 0 });
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
        return;
      }
      setError(err?.response?.data?.detail || "Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, [headers, onLogout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return enquiries;
    return enquiries.filter((e) =>
      [e.name, e.email, e.company, e.phone, e.plan_interest, e.monthly_volume, e.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [enquiries, query]);

  return (
    <div className="min-h-screen bg-delfi-cloud" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-delfi-navy text-white sticky top-0 z-30 shadow-[0_4px_24px_-8px_rgba(20,27,77,0.4)]">
        <div className="delfi-container flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Twasal Awasal" className="h-9 w-auto" />
            <div className="hidden md:block ms-2 ps-4 border-s border-white/15">
              <p className="text-[10px] uppercase tracking-[0.2em] text-delfi-red font-bold">Admin</p>
              <p className="text-sm font-semibold">Partner Enquiries</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={fetchData}
              className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-white/5"
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-white/5"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="delfi-container py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={BarChart3} label="Total enquiries" value={stats.total} />
          <StatCard icon={Calendar} label="Last 7 days" value={stats.last_7_days} />
          <StatCard
            icon={Mail}
            label="Notifications going to"
            value={<span className="text-[15px] font-bold text-delfi-navy break-all">support@delfioman.com</span>}
          />
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl border border-delfi-navy/8 p-4 mb-5 flex items-center gap-3">
          <Search size={16} className="text-delfi-navy/45" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, company, email, phone, plan, or message…"
            className="flex-1 bg-transparent focus:outline-none text-delfi-navy placeholder-delfi-navy/40 text-sm"
            data-testid="admin-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="text-xs text-delfi-navy/55 hover:text-delfi-red font-medium"
            >
              Clear
            </button>
          )}
          <span className="text-xs text-delfi-navy/45 font-medium">
            Showing {filtered.length} of {enquiries.length}
          </span>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 flex items-center gap-2 p-4 rounded-xl bg-delfi-red/10 border border-delfi-red/25 text-delfi-red text-sm">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-2xl border border-delfi-navy/8 overflow-hidden shadow-[0_2px_8px_-2px_rgba(46,46,46,0.06)]">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="admin-enquiries-table">
              <thead>
                <tr className="bg-delfi-navy text-white text-[11px] uppercase tracking-[0.16em] font-bold">
                  <th className="px-4 py-3.5">Received</th>
                  <th className="px-4 py-3.5">Company / Contact</th>
                  <th className="px-4 py-3.5">Email / Phone</th>
                  <th className="px-4 py-3.5">Plan</th>
                  <th className="px-4 py-3.5">Volume</th>
                  <th className="px-4 py-3.5"></th>
                </tr>
              </thead>
              <tbody>
                {loading && enquiries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-delfi-navy/50">
                      <Loader2 size={20} className="animate-spin inline-block" /> Loading enquiries…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center" data-testid="admin-empty-state">
                      <div className="inline-flex w-14 h-14 rounded-full bg-delfi-cloud items-center justify-center mb-4">
                        <Mail size={20} className="text-delfi-navy/45" />
                      </div>
                      <p className="text-delfi-navy font-semibold">
                        {query ? "No matching enquiries" : "No enquiries yet"}
                      </p>
                      <p className="text-delfi-navy/55 text-sm mt-1">
                        {query
                          ? "Try clearing the search or use different terms."
                          : "When partners submit the contact form, they'll appear here."}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => (
                    <EnquiryRow
                      key={row.id}
                      row={row}
                      expanded={expandedId === row.id}
                      onToggle={() => setExpandedId((cur) => (cur === row.id ? null : row.id))}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-6 text-xs text-delfi-navy/45 text-center">
          Twasal Awasal · Admin Console · Click any row to expand details.
        </p>
      </main>
    </div>
  );
}

export default function AdminEnquiries() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");

  const handleLogout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
  }, []);

  if (!token) return <LoginScreen onAuth={setToken} />;
  return <Dashboard token={token} onLogout={handleLogout} />;
}
